import React, {ReactElement, useCallback, useEffect, useRef, useState} from "react";
import {Circle, Ellipse, Group, Image as KonvaImage, Layer, Line, Rect, Stage, Text, Transformer} from "react-konva";
import useResizeObserver from "use-resize-observer";
import {colorBlue, colorYellow, gray, mediumGray, translucentWhite} from "@/shared/colors";
import {
  CanvasElement,
  CommentElement,
  ImageCanvasElement,
  Shape,
  ShapeCanvasElement,
  WORKBENCH_TOOL_KTM
} from "./types";
import Konva from "konva";
import {IconButton, Typography} from "@mui/material";
import {Comment, ZoomInOutlined, ZoomOutOutlined} from "@mui/icons-material";
import {PD_SM} from "@/shared/dimens";
import useImage from "use-image";

const DEFAULT_PAGE_WIDTH = 800;
const DEFAULT_PAGE_HEIGHT = 600;

const CANVAS_PADDING = 48;
const POLYGON_CLOSE_THRESHOLD = 12; // screen pixels

type MarqueeRect = { x: number, y: number, width: number, height: number } | null;
type RulerMeasurement = { x1: number, y1: number, x2: number, y2: number };
const RULER_TICK_INTERVAL = 25;

function rectsIntersect(
  a: { x: number, y: number, width: number, height: number },
  b: { x: number, y: number, width: number, height: number },
): boolean {
  return a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y;
}

export function WorkbenchCanvas(props: {
  elements: CanvasElement[];
  selectedIds: string[];
  selectedTool?: Shape;
  panning?: boolean;
  ruler?: boolean;
  invert?: boolean;
  crosshair?: boolean;
  imageUrl?: string;
  brightness?: number;
  contrast?: number;
  saturation?: number;
  rotation?: number;
  onSelect: (ids: string[]) => void;
  onElementUpdate: (id: string, x: number, y: number, width: number, height: number, rotation: number) => void;
  onElementAdd?: (element: ShapeCanvasElement, screenPos: { x: number, y: number }) => void;
  comments?: CommentElement[];
  commenting?: boolean;
  onCommentClick?: (pageX: number, pageY: number, screenPos: { x: number, y: number }) => void;
  onCommentSelect?: (id: string) => void;
}): ReactElement {
  const {
    elements,
    selectedIds,
    selectedTool,
    panning,
    ruler,
    crosshair,
    imageUrl,
    invert = false,
    brightness = 128,
    contrast = 128,
    saturation = 128,
    rotation = 0,
    onSelect,
    onElementUpdate,
    onElementAdd,
    comments = [],
    commenting,
    onCommentClick,
    onCommentSelect,
  } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const {width = 0, height = 0} = useResizeObserver({ref: containerRef});
  const trRef = useRef<Konva.Transformer>(null);
  const nodesRef = useRef<Map<string, Konva.Node>>(new Map());
  const rotationRef = useRef<number>(0);

  const [zoomLevel, setZoomLevel] = useState(0); // 0 = fit-to-viewport
  const [panOffset, setPanOffset] = useState<{ x: number, y: number }>({x: 0, y: 0});
  const panStartRef = useRef<{ x: number, y: number, panX: number, panY: number } | null>(null);
  const [marquee, setMarquee] = useState<MarqueeRect>(null);
  const marqueeStartRef = useRef<{ x: number, y: number } | null>(null);
  const [drawStart, setDrawStart] = useState<{ x: number, y: number } | null>(null);
  const [drawRect, setDrawRect] = useState<{ x: number, y: number, width: number, height: number } | null>(null);
  const [polygonPoints, setPolygonPoints] = useState<{ x: number, y: number }[]>([]);
  const [cursorPos, setCursorPos] = useState<{ x: number, y: number } | null>(null);
  const [mousePos, setMousePos] = useState<{ x: number, y: number } | null>(null);
  const [rulerMeasurements, setRulerMeasurements] = useState<RulerMeasurement[]>([]);
  const [rulerDraft, setRulerDraft] = useState<RulerMeasurement | null>(null);
  const rulerStartRef = useRef<{ x: number, y: number } | null>(null);
  const bgLayerRef = useRef<Konva.Layer>(null);
  const [bgImage] = useImage(imageUrl);
  const pageWidth = bgImage?.naturalWidth || DEFAULT_PAGE_WIDTH;
  const pageHeight = bgImage?.naturalHeight || DEFAULT_PAGE_HEIGHT;
  const fitScale = width > 0 && height > 0
    ? Math.min((width - CANVAS_PADDING * 2) / pageWidth, (height - CANVAS_PADDING * 2) / pageHeight)
    : 1;
  const scale = zoomLevel === 0 ? fitScale : zoomLevel;
  const zoomPercent = Math.round(scale * 100);
  const pageScaledWidth = pageWidth * scale;
  const pageScaledHeight = pageHeight * scale;
  const pageX = (width - pageScaledWidth) / 2 + panOffset.x;
  const pageY = (height - pageScaledHeight) / 2 + panOffset.y;

  const ZOOM_STEP = 0.1;
  const MIN_ZOOM = 0.1;
  const MAX_ZOOM = 5;

  function handleZoomIn() {
    const current = zoomLevel === 0 ? fitScale : zoomLevel;
    setZoomLevel(Math.min(MAX_ZOOM, Math.round((current + ZOOM_STEP) * 10) / 10));
  }

  function handleZoomOut() {
    const current = zoomLevel === 0 ? fitScale : zoomLevel;
    setZoomLevel(Math.max(MIN_ZOOM, Math.round((current - ZOOM_STEP) * 10) / 10));
  }

  function handleZoomReset() {
    setZoomLevel(0);
    setPanOffset({x: 0, y: 0});
  }

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    function handleWheel(e: WheelEvent) {
      if (!e.ctrlKey && !e.metaKey) {
        return;
      }
      e.preventDefault();
      const current = zoomLevel === 0 ? fitScale : zoomLevel;
      const delta = -e.deltaY * 0.01;
      const next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round((current + delta) * 100) / 100));
      setZoomLevel(next);
    }

    container.addEventListener("wheel", handleWheel, {passive: false});
    return () => container.removeEventListener("wheel", handleWheel);
  }, [zoomLevel, fitScale]);

  useEffect(() => {
    const canvas = bgLayerRef.current?.getCanvas()?._canvas as HTMLCanvasElement;
    if (canvas) {
      const filters: string[] = [];
      if (invert) filters.push("invert(1)");
      if (brightness !== 128) filters.push(`brightness(${brightness / 128})`);
      if (contrast !== 128) filters.push(`contrast(${contrast / 128})`);
      if (saturation !== 128) filters.push(`saturate(${saturation / 128})`);
      canvas.style.filter = filters.length > 0 ? filters.join(" ") : "";
    }
  }, [invert, brightness, contrast, saturation]);
  useEffect(() => {
    const canvas = bgLayerRef.current?.getCanvas()?._canvas as HTMLCanvasElement;
    if (canvas) {
      canvas.style.transform = `rotate(${rotation || 0}deg)`;
    }
    for (const el of elements) {
      if (el.type === "shape") {
        el.rotation += (rotation || 0) - rotationRef.current;
      }
    }
    rotationRef.current = rotation;
  }, [rotation, elements]);

  useEffect(() => {
    if (!ruler) {
      setRulerMeasurements([]);
      setRulerDraft(null);
      rulerStartRef.current = null;
    }
  }, [ruler]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape" && polygonPoints.length > 0) {
        setPolygonPoints([]);
        setCursorPos(null);
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [polygonPoints.length]);

  useEffect(() => {
    if (!trRef.current) {
      return;
    }
    const nodes: Konva.Node[] = [];
    for (const id of selectedIds) {
      const el = elements.find(e => e.id === id);
      const node = nodesRef.current.get(id);
      if (node && el && !el.locked) {
        const noTransform = el.type === "shape" && ((el as ShapeCanvasElement).shape === Shape.POLYGON || (el as ShapeCanvasElement).shape === Shape.POINT);
        if (!noTransform) {
          nodes.push(node);
        }
      }
    }
    trRef.current.nodes(nodes);
    trRef.current.getLayer()?.batchDraw();
  }, [selectedIds, elements]);

  function renderRuler(m: RulerMeasurement, key: string) {
    const sx1 = pageX + m.x1 * scale;
    const sy1 = pageY + m.y1 * scale;
    const sx2 = pageX + m.x2 * scale;
    const sy2 = pageY + m.y2 * scale;
    const dx = m.x2 - m.x1;
    const dy = m.y2 - m.y1;
    const dist = Math.hypot(dx, dy);
    const angle = Math.atan2(dy, dx);
    const perpX = -Math.sin(angle);
    const perpY = Math.cos(angle);
    const tickLen = 6;
    const ticks: ReactElement[] = [];
    const tickCount = Math.floor(dist / RULER_TICK_INTERVAL);
    for (let i = 0; i <= tickCount; i++) {
      const t = i * RULER_TICK_INTERVAL;
      const tx = pageX + (m.x1 + Math.cos(angle) * t) * scale;
      const ty = pageY + (m.y1 + Math.sin(angle) * t) * scale;
      ticks.push(<Line
        key={`${key}_tick_${i}`}
        points={[tx + perpX * tickLen, ty + perpY * tickLen, tx - perpX * tickLen, ty - perpY * tickLen]}
        stroke={translucentWhite}
        strokeWidth={1}
        listening={false}
      />);
    }
    const midX = (sx1 + sx2) / 2;
    const midY = (sy1 + sy2) / 2;
    const labelOffsetX = perpX * 14;
    const labelOffsetY = perpY * 14;
    return <Group key={key} listening={false}>
      <Line points={[sx1, sy1, sx2, sy2]} stroke={translucentWhite} strokeWidth={1.5} listening={false}/>
      {ticks}
      <Circle x={sx1} y={sy1} radius={3} fill={translucentWhite} listening={false}/>
      <Circle x={sx2} y={sy2} radius={3} fill={translucentWhite} listening={false}/>
      <Text
        x={midX + labelOffsetX - 30}
        y={midY + labelOffsetY - 8}
        width={60}
        align="center"
        text={`${Math.round(dist)}px`}
        fontSize={12}
        fill={translucentWhite}
        listening={false}
      />
    </Group>;
  }

  const setNodeRef = useCallback((id: string, node: Konva.Node | null) => {
    if (node) {
      nodesRef.current.set(id, node);
    } else {
      nodesRef.current.delete(id);
    }
  }, []);

  return (
    <div style={{position: "relative", flexGrow: 1, display: "flex", flexDirection: "column", overflow: "hidden"}}>
      <div
        ref={containerRef}
        style={{
          flexGrow: 1,
          overflow: "auto",
          background: gray,
          position: "relative",
          cursor: panning ? "grab" : ruler || commenting ? "crosshair" : "default",
        }}
      >
        {width > 0 && height > 0 && (
          // @ts-ignore
          <Stage
            width={width}
            height={height}
            onMouseDown={(e) => {
              const target = e.target;
              const stage = target.getStage();
              const pos = stage?.getPointerPosition();
              if (panning && pos) {
                panStartRef.current = {x: pos.x, y: pos.y, panX: panOffset.x, panY: panOffset.y};
                return;
              }
              if (ruler && pos) {
                const px = (pos.x - pageX) / scale;
                const py = (pos.y - pageY) / scale;
                rulerStartRef.current = {x: px, y: py};
                return;
              }
              if (target === target.getStage() || target.name() === "page-bg") {
                if (selectedTool === Shape.POLYGON && onElementAdd && pos) {
                  const px = (pos.x - pageX) / scale;
                  const py = (pos.y - pageY) / scale;
                  if (polygonPoints.length >= 3) {
                    const firstScreenX = pageX + polygonPoints[0].x * scale;
                    const firstScreenY = pageY + polygonPoints[0].y * scale;
                    const dist = Math.hypot(pos.x - firstScreenX, pos.y - firstScreenY);
                    if (dist < POLYGON_CLOSE_THRESHOLD) {
                      const pts = polygonPoints;
                      const xs = pts.map(p => p.x);
                      const ys = pts.map(p => p.y);
                      const minX = Math.min(...xs);
                      const minY = Math.min(...ys);
                      const maxX = Math.max(...xs);
                      const maxY = Math.max(...ys);
                      const relPoints = pts.flatMap(p => [p.x - minX, p.y - minY]);
                      onElementAdd({
                        id: `shape_${Date.now()}`,
                        name: WORKBENCH_TOOL_KTM.findMetadata(selectedTool).text,
                        type: "shape",
                        shape: Shape.POLYGON,
                        x: minX,
                        y: minY,
                        width: maxX - minX,
                        height: maxY - minY,
                        points: relPoints,
                        rotation: 0,
                        visible: true,
                        locked: false,
                      }, pos);
                      setPolygonPoints([]);
                      setCursorPos(null);
                      return;
                    }
                  }
                  setPolygonPoints(prev => [...prev, {x: px, y: py}]);
                  return;
                }
                if (selectedTool === Shape.POINT && onElementAdd && pos) {
                  const px = (pos.x - pageX) / scale;
                  const py = (pos.y - pageY) / scale;
                  onElementAdd({
                    id: `shape_${Date.now()}`,
                    name: WORKBENCH_TOOL_KTM.findMetadata(selectedTool).text,
                    type: "shape",
                    shape: Shape.POINT,
                    x: px,
                    y: py,
                    width: 0,
                    height: 0,
                    rotation: 0,
                    visible: true,
                    locked: false,
                  }, pos);
                  return;
                }
                if (commenting && onCommentClick && pos) {
                  const px = (pos.x - pageX) / scale;
                  const py = (pos.y - pageY) / scale;
                  onCommentClick(px, py, pos);
                  return;
                }
                onSelect([]);
                if (selectedTool && onElementAdd && pos) {
                  const px = (pos.x - pageX) / scale;
                  const py = (pos.y - pageY) / scale;
                  setDrawStart({x: px, y: py});
                } else if (pos) {
                  marqueeStartRef.current = {x: pos.x, y: pos.y};
                }
              }
            }}
            onMouseMove={(e) => {
              const stage = e.target.getStage();
              const pos = stage?.getPointerPosition();
              if (!pos) return;
              if (crosshair) setMousePos(pos);
              if (panStartRef.current) {
                const dx = pos.x - panStartRef.current.x;
                const dy = pos.y - panStartRef.current.y;
                setPanOffset({x: panStartRef.current.panX + dx, y: panStartRef.current.panY + dy});
                return;
              }
              if (rulerStartRef.current) {
                const px = (pos.x - pageX) / scale;
                const py = (pos.y - pageY) / scale;
                setRulerDraft({x1: rulerStartRef.current.x, y1: rulerStartRef.current.y, x2: px, y2: py});
                return;
              }
              if (selectedTool === Shape.POLYGON && polygonPoints.length > 0) {
                setCursorPos({x: (pos.x - pageX) / scale, y: (pos.y - pageY) / scale});
                return;
              }
              if (drawStart) {
                const px = (pos.x - pageX) / scale;
                const py = (pos.y - pageY) / scale;
                setDrawRect({
                  x: Math.min(drawStart.x, px),
                  y: Math.min(drawStart.y, py),
                  width: Math.abs(px - drawStart.x),
                  height: Math.abs(py - drawStart.y),
                });
                return;
              }
              if (!marqueeStartRef.current) return;
              const start = marqueeStartRef.current;
              setMarquee({
                x: Math.min(start.x, pos.x),
                y: Math.min(start.y, pos.y),
                width: Math.abs(pos.x - start.x),
                height: Math.abs(pos.y - start.y),
              });
            }}
            onMouseUp={(e) => {
              const upPos = e.target.getStage()?.getPointerPosition();
              if (panStartRef.current) {
                panStartRef.current = null;
                return;
              }
              if (rulerStartRef.current && rulerDraft) {
                const dist = Math.hypot(rulerDraft.x2 - rulerDraft.x1, rulerDraft.y2 - rulerDraft.y1);
                if (dist > 3) {
                  setRulerMeasurements(prev => [...prev, rulerDraft]);
                }
                rulerStartRef.current = null;
                setRulerDraft(null);
                return;
              }
              if (rulerStartRef.current) {
                rulerStartRef.current = null;
                return;
              }
              if (drawStart && drawRect && drawRect.width > 3 && drawRect.height > 3 && selectedTool && onElementAdd) {
                const id = `shape_${Date.now()}`;
                const newElement: ShapeCanvasElement = {
                  id,
                  name: WORKBENCH_TOOL_KTM.findMetadata(selectedTool).text,
                  type: "shape",
                  shape: selectedTool,
                  x: drawRect.x,
                  y: drawRect.y,
                  width: drawRect.width,
                  height: drawRect.height,
                  rotation: 0,
                  visible: true,
                  locked: false,
                };
                onElementAdd(newElement, upPos || {x: pageX + drawRect.x * scale, y: pageY + drawRect.y * scale});
              }
              setDrawStart(null);
              setDrawRect(null);
              if (marquee && marquee.width > 3 && marquee.height > 3) {
                const mPageX = (marquee.x - pageX) / scale;
                const mPageY = (marquee.y - pageY) / scale;
                const mPageW = marquee.width / scale;
                const mPageH = marquee.height / scale;
                const mRect = {x: mPageX, y: mPageY, width: mPageW, height: mPageH};
                const hitIds = elements
                  .filter(el => el.visible && !el.locked && !el.id.startsWith("bg_") && rectsIntersect(mRect, el))
                  .map(el => el.id);
                if (hitIds.length > 0) {
                  onSelect(hitIds);
                }
              }
              marqueeStartRef.current = null;
              setMarquee(null);
            }}
            onMouseLeave={() => {
              if (crosshair) setMousePos(null);
            }}>
            <Layer ref={bgLayerRef}>
              <Rect
                name="page-bg"
                x={pageX}
                y={pageY}
                width={pageScaledWidth}
                height={pageScaledHeight}
                fill="white"
                shadowColor="rgba(0,0,0,0.5)"
                shadowBlur={2}
                shadowOffsetX={0}
                shadowOffsetY={0}
                shadowEnabled
              />
              {bgImage && (
                <KonvaImage
                  image={bgImage}
                  x={pageX}
                  y={pageY}
                  width={pageScaledWidth}
                  height={pageScaledHeight}
                  listening={false}
                />
              )}
            </Layer>
            <Layer>
              {elements.map(el => {
                if (!el.visible) {
                  return null;
                }
                const isBackground = el.id.startsWith("bg_");
                if (isBackground) {
                  // Background rendered as non-interactive image
                  if (el.type === "image") {
                    const imgEl = el as ImageCanvasElement;
                    if (!imgEl.htmlImage) return null;
                    return <KonvaImage
                      key={el.id}
                      image={imgEl.htmlImage}
                      x={pageX + el.x * scale}
                      y={pageY + el.y * scale}
                      width={el.width * scale}
                      height={el.height * scale}
                      listening={false}
                    />;
                  }
                  return null;
                }
                const otherElements = elements.filter(o => o.id !== el.id && o.visible && !o.id.startsWith("bg_"));
                const commonProps = {
                  key: el.id,
                  ref: (node: Konva.Node | null) => setNodeRef(el.id, node),
                  rotation: el.rotation,
                  draggable: !el.locked,
                  onClick: () => onSelect([el.id]),
                  onTap: () => onSelect([el.id]),
                  onDragMove: (e: Konva.KonvaEventObject<DragEvent>) => {
                  },
                  onDragEnd: (e: Konva.KonvaEventObject<DragEvent>) => {
                    onElementUpdate(
                      el.id,
                      (e.target.x() - pageX) / scale,
                      (e.target.y() - pageY) / scale,
                      el.width,
                      el.height,
                      el.rotation,
                    );
                  },
                  onTransformEnd: (e: Konva.KonvaEventObject<Event>) => {
                    const node = e.target;
                    const scaleX = node.scaleX();
                    const scaleY = node.scaleY();
                    node.scaleX(1);
                    node.scaleY(1);
                    onElementUpdate(
                      el.id,
                      (node.x() - pageX) / scale,
                      (node.y() - pageY) / scale,
                      Math.max(5, node.width() * scaleX) / scale,
                      Math.max(5, node.height() * scaleY) / scale,
                      node.rotation(),
                    );
                  },
                };
                if (el.type === "image") {
                  const imgEl = el as ImageCanvasElement;
                  if (!imgEl.htmlImage) {
                    return null;
                  }
                  return <KonvaImage
                    {...commonProps}
                    image={imgEl.htmlImage}
                    x={pageX + el.x * scale}
                    y={pageY + el.y * scale}
                    width={el.width * scale}
                    height={el.height * scale}
                  />;
                }
                if (el.type === "shape") {
                  const shapeEl = el as ShapeCanvasElement;
                  if (shapeEl.shape === Shape.RECTANGLE) {
                    return <Rect
                      {...commonProps}
                      x={pageX + el.x * scale}
                      y={pageY + el.y * scale}
                      width={el.width * scale}
                      height={el.height * scale}
                      fill={WORKBENCH_TOOL_KTM.findMetadata(Shape.RECTANGLE).shapeFill}
                      stroke={colorBlue}
                      strokeWidth={2}
                    />;
                  }
                  if (shapeEl.shape === Shape.ELLIPSE) {
                    const rX = (el.width / 2) * scale;
                    const rY = (el.height / 2) * scale;
                    return <Ellipse
                      {...commonProps}
                      x={pageX + el.x * scale + rX}
                      y={pageY + el.y * scale + rY}
                      radiusX={rX}
                      radiusY={rY}
                      fill={WORKBENCH_TOOL_KTM.findMetadata(Shape.ELLIPSE).shapeFill}
                      stroke={colorBlue}
                      strokeWidth={2}
                      onDragEnd={(e: Konva.KonvaEventObject<DragEvent>) => {
                        const node = e.target;
                        onElementUpdate(
                          el.id,
                          (node.x() - rX - pageX) / scale,
                          (node.y() - rY - pageY) / scale,
                          el.width,
                          el.height,
                          el.rotation,
                        );
                      }}
                      onTransformEnd={(e: Konva.KonvaEventObject<Event>) => {
                        const node = e.target as Konva.Ellipse;
                        const sx = node.scaleX();
                        const sy = node.scaleY();
                        const newW = Math.max(5, node.radiusX() * sx * 2) / scale;
                        const newH = Math.max(5, node.radiusY() * sy * 2) / scale;
                        node.scaleX(1);
                        node.scaleY(1);
                        onElementUpdate(
                          el.id,
                          (node.x() - pageX) / scale - newW / 2,
                          (node.y() - pageY) / scale - newH / 2,
                          newW,
                          newH,
                          node.rotation(),
                        );
                      }}
                    />;
                  }
                  if (shapeEl.shape === Shape.POLYGON && shapeEl.points) {
                    const scaledPoints = [];
                    for (let i = 0; i < shapeEl.points.length; i += 2) {
                      scaledPoints.push(shapeEl.points[i] * scale);
                      scaledPoints.push(shapeEl.points[i + 1] * scale);
                    }
                    return <Line
                      {...commonProps}
                      x={pageX + el.x * scale}
                      y={pageY + el.y * scale}
                      points={scaledPoints}
                      closed={true}
                      fill={WORKBENCH_TOOL_KTM.findMetadata(Shape.POLYGON).shapeFill}
                      stroke={colorBlue}
                      strokeWidth={2}
                    />;
                  }
                  if (shapeEl.shape === Shape.POINT) {
                    return <Circle
                      key={el.id}
                      ref={(node: Konva.Node | null) => setNodeRef(el.id, node)}
                      x={pageX + el.x * scale}
                      y={pageY + el.y * scale}
                      radius={4}
                      fill={WORKBENCH_TOOL_KTM.findMetadata(Shape.POINT).shapeFill}
                      draggable={!el.locked}
                      onClick={() => onSelect([el.id])}
                      onTap={() => onSelect([el.id])}
                      onDragEnd={(e: Konva.KonvaEventObject<DragEvent>) => {
                        onElementUpdate(
                          el.id,
                          (e.target.x() - pageX) / scale,
                          (e.target.y() - pageY) / scale,
                          0,
                          0,
                          0,
                        );
                      }}
                    />;
                  }
                }
                return null;
              })}
              <Transformer
                ref={trRef}
                boundBoxFunc={(oldBox: Konva.Box, newBox: Konva.Box) => {
                  if (Math.abs(newBox.width) < 5 || Math.abs(newBox.height) < 5) {
                    return oldBox;
                  }
                  return newBox;
                }}
              />
              {drawRect && selectedTool === Shape.RECTANGLE && (
                <Rect
                  x={pageX + drawRect.x * scale}
                  y={pageY + drawRect.y * scale}
                  width={drawRect.width * scale}
                  height={drawRect.height * scale}
                  fill="rgba(0, 102, 255, 0.15)"
                  stroke={colorBlue}
                  strokeWidth={1}
                  dash={[4, 4]}
                  listening={false}
                />
              )}
              {drawRect && selectedTool === Shape.ELLIPSE && (
                <Ellipse
                  x={pageX + (drawRect.x + drawRect.width / 2) * scale}
                  y={pageY + (drawRect.y + drawRect.height / 2) * scale}
                  radiusX={(drawRect.width / 2) * scale}
                  radiusY={(drawRect.height / 2) * scale}
                  fill="rgba(0, 102, 255, 0.15)"
                  stroke={colorBlue}
                  strokeWidth={1}
                  dash={[4, 4]}
                  listening={false}
                />
              )}
              {polygonPoints.length > 0 && (
                <>
                  <Line
                    points={[
                      ...polygonPoints.flatMap(p => [pageX + p.x * scale, pageY + p.y * scale]),
                      ...(cursorPos ? [pageX + cursorPos.x * scale, pageY + cursorPos.y * scale] : []),
                    ]}
                    stroke={colorBlue}
                    strokeWidth={1}
                    dash={[4, 4]}
                    listening={false}
                  />
                  {polygonPoints.map((p, i) => (
                    <Circle
                      key={`poly_pt_${i}`}
                      x={pageX + p.x * scale}
                      y={pageY + p.y * scale}
                      radius={i === 0 ? 6 : 3}
                      fill={i === 0 ? colorBlue : "white"}
                      stroke={colorBlue}
                      strokeWidth={1}
                      listening={false}
                    />
                  ))}
                  {cursorPos && polygonPoints.length >= 3 && (() => {
                    const firstScreenX = pageX + polygonPoints[0].x * scale;
                    const firstScreenY = pageY + polygonPoints[0].y * scale;
                    const cursorScreenX = pageX + cursorPos.x * scale;
                    const cursorScreenY = pageY + cursorPos.y * scale;
                    const dist = Math.hypot(cursorScreenX - firstScreenX, cursorScreenY - firstScreenY);
                    if (dist < POLYGON_CLOSE_THRESHOLD) {
                      return <Circle
                        x={firstScreenX}
                        y={firstScreenY}
                        radius={8}
                        fill="rgba(0, 102, 255, 0.3)"
                        stroke={colorBlue}
                        strokeWidth={2}
                        listening={false}
                      />;
                    }
                    return null;
                  })()}
                </>
              )}
              {marquee && (
                <Rect
                  x={marquee.x}
                  y={marquee.y}
                  width={marquee.width}
                  height={marquee.height}
                  fill="rgba(0, 102, 255, 0.1)"
                  stroke="#0066ff"
                  strokeWidth={1}
                  dash={[4, 4]}
                  listening={false}
                />
              )}
              {rulerMeasurements.map((m, i) => renderRuler(m, `ruler_${i}`))}
              {rulerDraft && renderRuler(rulerDraft, "ruler_draft")}
              {crosshair && mousePos && <>
                <Line points={[mousePos.x, 0, mousePos.x, height]}
                      stroke={"#0f0"}
                      strokeWidth={1}
                      dash={[4, 4]}
                      listening={false}/>
                <Line points={[0, mousePos.y, width, mousePos.y]}
                      stroke={"#0f0"} strokeWidth={1}
                      dash={[4, 4]}
                      listening={false}/>
              </>}
            </Layer>
          </Stage>
        )}
        {comments.map(c => {
          const sx = pageX + c.x * scale;
          const sy = pageY + c.y * scale;
          return <Comment
            key={c.id}
            onClick={() => onCommentSelect?.(c.id)}
            style={{
              position: "absolute",
              left: sx - 12,
              top: sy - 12,
              fontSize: 24,
              color: colorYellow,
              cursor: "pointer",
              pointerEvents: "auto",
            }}
          />;
        })}
      </div>
      <div style={{
        position: "absolute",
        bottom: 0,
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: PD_SM,
        padding: `${PD_SM}px 0`,
        background: gray,
      }}>
        <IconButton size="small" onClick={handleZoomOut} disabled={zoomPercent <= Math.round(MIN_ZOOM * 100)}>
          <ZoomOutOutlined fontSize="small"/>
        </IconButton>
        <Typography
          variant="body2"
          onClick={handleZoomReset}
          style={{
            cursor: "pointer",
            minWidth: 48,
            textAlign: "center",
            color: mediumGray,
            userSelect: "none",
          }}>
          {zoomPercent}%
        </Typography>
        <IconButton size="small" onClick={handleZoomIn} disabled={zoomPercent >= Math.round(MAX_ZOOM * 100)}>
          <ZoomInOutlined fontSize="small"/>
        </IconButton>
      </div>
    </div>
  );
}
